import { Link } from 'react-router-dom'

export type BrandVariant = 'responsive' | 'horizontal' | 'compact' | 'symbol' | 'primary'
export type BrandTone = 'navy' | 'reversed'

type CommonBrandProps = {
  to?: string
  contextLabel?: string
  className?: string
  ariaHidden?: boolean
}

type BrandProps = CommonBrandProps &
  (
    | { variant?: BrandVariant; tone?: 'navy' }
    | { variant: 'horizontal' | 'compact'; tone: 'reversed' }
  )

const dimensions: Record<Exclude<BrandVariant, 'responsive'>, { width: number; height: number }> = {
  horizontal: { width: 1400, height: 400 },
  compact: { width: 1120, height: 320 },
  symbol: { width: 700, height: 840 },
  primary: { width: 900, height: 1091 },
}

function assetPath(variant: Exclude<BrandVariant, 'responsive'>, tone: BrandTone) {
  const suffix = tone === 'reversed' ? '-reversed' : ''
  return `/brand/saf-bestari-${variant === 'compact' ? 'horizontal-compact' : variant}${suffix}.svg`
}

function BrandImage({
  variant,
  tone,
}: {
  variant: Exclude<BrandVariant, 'responsive'>
  tone: BrandTone
}) {
  const { width, height } = dimensions[variant]
  return (
    <img
      className="brand__image"
      src={assetPath(variant, tone)}
      width={width}
      height={height}
      alt=""
      decoding="async"
    />
  )
}

export function Brand({
  variant = 'responsive',
  tone = 'navy',
  to = '/',
  contextLabel,
  className,
  ariaHidden = false,
}: BrandProps) {
  const accessibleName = contextLabel
    ? `SAMBEST Alumni Federation — ${contextLabel}`
    : 'SAMBEST Alumni Federation'
  const classes = ['brand', `brand--${variant}`, `brand--${tone}`, className]
    .filter(Boolean)
    .join(' ')

  return (
    <Link
      className={classes}
      to={to}
      aria-label={ariaHidden ? undefined : accessibleName}
      aria-hidden={ariaHidden || undefined}
      tabIndex={ariaHidden ? -1 : undefined}
    >
      {variant === 'responsive' ? (
        <picture className="brand__picture">
          <source media="(max-width: 639px)" srcSet={assetPath('symbol', 'navy')} />
          <source media="(max-width: 1023px)" srcSet={assetPath('compact', 'navy')} />
          <BrandImage variant="horizontal" tone="navy" />
        </picture>
      ) : (
        <BrandImage variant={variant} tone={tone} />
      )}
      {contextLabel ? (
        <span className="brand__context" aria-hidden="true">
          {contextLabel}
        </span>
      ) : null}
    </Link>
  )
}
